import { useEffect } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { HwcgContactListMember } from 'hooks/hwcgContactLists/hwcgContactListHooks';
import { useAppSelector } from 'slices/commonSelectors';

import {
  hwcgAuthorizationComplete,
  type HwcgAuthorizationState,
} from './hwcgAuthorization';

function cell(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  return trimmed || '—';
}

export function HwcgAuthorizationSection({
  companyName,
  specificsDate,
  specificsTime,
  people,
  authorization,
  onChange,
  readOnly = false,
}: {
  companyName: string;
  specificsDate: string;
  specificsTime: string;
  people: HwcgContactListMember[];
  authorization: HwcgAuthorizationState;
  onChange: (next: HwcgAuthorizationState) => void;
  readOnly?: boolean;
}) {
  const currentUser = useAppSelector((state) => state.app.user);
  const callerDefault =
    (currentUser?.user_name as string | undefined)?.trim() ||
    (currentUser?.email_address as string | undefined)?.trim() ||
    '';

  useEffect(() => {
    if (readOnly) return;
    const next = { ...authorization };
    let changed = false;
    if (!next.date.trim() && specificsDate.trim()) {
      next.date = specificsDate;
      changed = true;
    }
    if (!next.time.trim() && specificsTime.trim()) {
      next.time = specificsTime;
      changed = true;
    }
    if (!next.caller.trim() && callerDefault) {
      next.caller = callerDefault;
      changed = true;
    }
    if (changed) onChange(next);
  }, [
    authorization,
    callerDefault,
    onChange,
    readOnly,
    specificsDate,
    specificsTime,
  ]);

  const company = companyName.trim();
  const options = people.map((person) => ({
    value: person.id,
    label: person.name,
  }));
  const selected =
    options.find((option) => option.value === authorization.contactId) ?? null;
  const missingIndividual = !authorization.contactId.trim();
  const missingDate = !authorization.date.trim();
  const missingTime = !authorization.time.trim();
  const missingCaller = !authorization.caller.trim();
  const ready = hwcgAuthorizationComplete(authorization);

  return (
    <div className="tw:space-y-4">
      <section className="tw:space-y-2">
        <div>
          <h3 className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
            Authorized individuals
            {company ? ` — ${company}` : ''}
          </h3>
          <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
            Marked authorized individual on the contact list.
          </p>
        </div>
        <div className="tw:rounded-md tw:border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone number</TableHead>
                <TableHead>Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {people.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="tw:text-muted-foreground">
                    No authorized individuals are marked on the contact list.
                  </TableCell>
                </TableRow>
              ) : (
                people.map((person) => (
                  <TableRow key={person.id}>
                    <TableCell>{person.name}</TableCell>
                    <TableCell>{cell(person.phone)}</TableCell>
                    <TableCell>{cell(person.email)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="tw:space-y-3">
        <div>
          <h3 className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
            Authorization call log
          </h3>
          <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
            Required — outlined in red until complete. The notice cannot be sent
            until every red field and the authorization confirmation are done.
          </p>
        </div>
        <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2 tw:lg:grid-cols-4">
          <div className="tw:grid tw:gap-1.5">
            <Label htmlFor="hwcg-auth-date">
              Date <span className="tw:text-destructive">*</span>
            </Label>
            <Input
              id="hwcg-auth-date"
              value={authorization.date}
              disabled={readOnly}
              aria-invalid={readOnly ? undefined : missingDate}
              className={cn(
                !readOnly && missingDate && 'tw:border-destructive'
              )}
              onChange={(event) =>
                onChange({ ...authorization, date: event.target.value })
              }
            />
          </div>
          <div className="tw:grid tw:gap-1.5">
            <Label htmlFor="hwcg-auth-time">
              Time <span className="tw:text-destructive">*</span>
            </Label>
            <Input
              id="hwcg-auth-time"
              value={authorization.time}
              disabled={readOnly}
              aria-invalid={readOnly ? undefined : missingTime}
              className={cn(
                !readOnly && missingTime && 'tw:border-destructive'
              )}
              onChange={(event) =>
                onChange({ ...authorization, time: event.target.value })
              }
            />
          </div>
          <div className="tw:grid tw:gap-1.5">
            <Label htmlFor="hwcg-auth-individual">
              Authorized individual{' '}
              <span className="tw:text-destructive">*</span>
            </Label>
            <SearchableSelect
              id="hwcg-auth-individual"
              aria-label="Authorized individual"
              options={options}
              value={selected}
              disabled={readOnly}
              invalid={!readOnly && missingIndividual}
              placeholder="Select individual..."
              contentClassName="dark"
              positionerClassName="tw:z-[60]"
              onChange={(next) =>
                onChange({ ...authorization, contactId: next?.value ?? '' })
              }
            />
          </div>
          <div className="tw:grid tw:gap-1.5">
            <Label htmlFor="hwcg-auth-caller">
              HWCG caller <span className="tw:text-destructive">*</span>
            </Label>
            <Input
              id="hwcg-auth-caller"
              value={authorization.caller}
              disabled={readOnly}
              aria-invalid={readOnly ? undefined : missingCaller}
              className={cn(
                !readOnly && missingCaller && 'tw:border-destructive'
              )}
              onChange={(event) =>
                onChange({ ...authorization, caller: event.target.value })
              }
            />
          </div>
        </div>
        <div className="tw:grid tw:gap-1.5">
          <Label htmlFor="hwcg-auth-description">
            Description <span className="tw:text-destructive">*</span>
          </Label>
          <Textarea
            id="hwcg-auth-description"
            rows={3}
            disabled={readOnly}
            placeholder="Optional notes about the call — context, instructions given, follow-ups..."
            value={authorization.description}
            onChange={(event) =>
              onChange({ ...authorization, description: event.target.value })
            }
          />
        </div>
        <label
          className={cn(
            'tw:flex tw:items-start tw:gap-2 tw:rounded-md tw:border tw:px-3 tw:py-2 tw:text-sm',
            authorization.confirmed || readOnly
              ? 'tw:border-border'
              : 'tw:border-destructive'
          )}
        >
          <Checkbox
            className="tw:mt-0.5"
            checked={authorization.confirmed}
            disabled={readOnly}
            aria-invalid={readOnly ? undefined : !authorization.confirmed}
            onCheckedChange={(value) =>
              onChange({ ...authorization, confirmed: value === true })
            }
          />
          <span>
            HWCG personnel has contacted and received authorization from
            Authorized Individual <span className="tw:text-destructive">*</span>
          </span>
        </label>
        {ready || readOnly ? null : (
          <p className="tw:text-xs tw:text-destructive">
            The notice cannot be sent until every red field and the
            authorization confirmation are done.
          </p>
        )}
      </section>
    </div>
  );
}
